import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent, useEffectItem} from 'helpers/hooks/utils';
import {useTable} from 'components/organisms/Providers/TableProvider/TableProvider';
import StyledEntitiesTableViews from 'components/organisms/TableViews/EntitiesTableViews/EntitiesTableViews.styles';
import utils from 'helpers/utils';
import {linkTo, useLinkNavigate} from 'helpers/hooks/links';
import {useLocation} from 'react-router-dom';
import ShareDialog from 'components/organisms/Dialogs/ShareDialog/ShareDialog';
import {useAuthUser} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import {useCommentCreate} from 'services/comment/comment.utils';
import {useDialogControl} from 'components/organisms/Providers/DialogProvider/DialogProvider';
import constants from 'helpers/constants';

const EntitiesTableViews = React.forwardRef((props, ref) => {
  const {
    collection,
    ...innerProps
  } = useComponentProps(props, 'EntitiesTableViews');

  const user = useAuthUser();
  const tableProvider = useTable();
  const views = tableProvider.views;

  const location = useLocation();
  const navigate = useLinkNavigate();
  const dialogControl = useDialogControl();

  const commentCreate = useCommentCreate();
  const createViewEvent = useEffectEvent(tableProvider.updaters?.createView);
  const updateViewEvent = useEffectEvent(tableProvider.updaters?.updateView);
  const deleteViewEvent = useEffectEvent(tableProvider.updaters?.deleteView);

  const handleShow = (e, view) => {
    const isCurrent = +tableProvider.view?.userView?.viewId === +view.viewId;

    tableProvider.closeViews();
    if (isCurrent) {
      tableProvider.listState.setView(null);
    }
    navigate({
      event: e,
      to: `?view=${view.viewId}`,
      relative: 'path',
      resetSearchParams: true
    });
  };

  const handleShare = (e, view) => {
    const handleSubmit = (data) => {
      const comment = data.comment;
      const recipients = data.recipients;

      const subscriptions = utils.clone(view?.subscriptions, true);
      recipients.forEach((recipient) => {
        if (!subscriptions.find((subscription) => +subscription.userId === +recipient.value)) {
          subscriptions.push({
            userId: +recipient.value
          })
        }
      })

      return updateViewEvent?.(view, {subscriptions: subscriptions})
        .then(() => {
          return commentCreate(comment, recipients, collection?.collectionId);
        });
    }

    const link = linkTo({to: `?view=${view.viewId}`, relative: 'path'}, location, true);
    const comment = `**${user?.firstName}** has shared this view with you [${view.name}](${link})`
    dialogControl.show(<ShareDialog type="view"
                                    comment={comment}
                                    onSubmit={handleSubmit} />, true);
  };

  const handleDelete = (e, view) => {
    return deleteViewEvent?.(view)
      .then(() => {
        if (+tableProvider.view?.userView?.viewId === +view.viewId) {
          navigate({event: e, to: location.pathname, resetSearchParams: true});
        }
      });
  };

  const handleSubmit = (view, values) => {
    const changes = values;

    changes.subscriptions = utils.clone(values.subscriptions ?? view.subscriptions ?? [], true);

    let subscription = changes.subscriptions.find((s) => +s.userId === +user?.userId);
    if (!subscription) {
      subscription = {
        userId: +user?.userId,
      };
      changes.subscriptions = changes.subscriptions.concat([subscription]);
    }
    subscription.notification = values.notification ?? false;
    delete changes.notification;

    if (!(+view.viewId > 0)) {
      changes.relationType = collection ? constants.view.relationTypes.collection : constants.view.relationTypes.database;
      changes.relationId = collection ? collection.collectionId : 0;
      changes.settings = tableProvider.viewSettings();

      return createViewEvent?.(changes)
        .then((res) => {
          const view = utils.camelcase(res.response.data.data);
          tableProvider.listState.setView({view: view.viewId, appliedAt: (new Date(view.updatedAt)).toISOString()});
        });
    } else {
      if (+tableProvider.view?.userView?.viewId === +view.viewId) {
        changes.settings = tableProvider.viewSettings();
      }

      return updateViewEvent?.(view, changes);
    }
  };

  const handleClose = () => {
    tableProvider.closeViews();
  }

  const viewSettings = useEffectItem(tableProvider.viewSettings());
  const isDirty = useMemo(() => {
    if (tableProvider.view?.userView && tableProvider.view?.userView?.viewId > 0) {
      return utils.viewChanged(viewSettings, tableProvider.view?.userView?.settings);
    } else {
      return false;
    }
  }, [tableProvider.view?.userView, viewSettings]);

  return <StyledEntitiesTableViews ref={ref} {...innerProps}
                                   view={tableProvider.view?.userView}
                                   views={views?.data}
                                   onClose={handleClose}
                                   onShow={handleShow}
                                   onShare={handleShare}
                                   onDelete={handleDelete}
                                   onSubmit={handleSubmit}
                                   isDirty={isDirty}
                                   isLoading={views?.status?.isLoading}>
    {innerProps.children}
  </StyledEntitiesTableViews>
});

EntitiesTableViews.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  filterGroups: PropTypes.array
};

export default EntitiesTableViews;
